import React, { useState } from 'react';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Fade,
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import DarkModeToggle from './DarkModeToggle';

const LanguageSelector: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const [hoveredLang, setHoveredLang] = useState<'en' | 'ar' | null>(null);

  const handleLanguageChange = (
    event: React.MouseEvent<HTMLElement>,
    newLanguage: 'en' | 'ar' | null
  ) => {
    if (newLanguage !== null) {
      setLanguage(newLanguage);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        position: 'relative',
      }}
    >
      {/* Dark Mode Toggle */}
      <DarkModeToggle />
      
      {/* Language Toggle */}
      <ToggleButtonGroup
        value={language}
        exclusive
        onChange={handleLanguageChange}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            px: 2,
            py: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: 'action.hover',
              transform: 'translateY(-1px)',
              boxShadow: 2,
            },
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderColor: 'primary.main',
              boxShadow: (theme) =>
                `0 4px 12px ${theme.palette.primary.main}40`,
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'translateY(-2px)',
                boxShadow: (theme) =>
                  `0 6px 16px ${theme.palette.primary.main}60`,
              },
            },
            '&:not(.Mui-selected)': {
              bgcolor: 'background.paper',
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
          },
        }}
      >
        <Tooltip
          title="English"
          arrow
          TransitionComponent={Fade}
          placement="top"
        >
          <ToggleButton
            value="en"
            onMouseEnter={() => setHoveredLang('en')}
            onMouseLeave={() => setHoveredLang(null)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                transition: 'transform 0.2s',
                transform:
                  hoveredLang === 'en' && language !== 'en'
                    ? 'scale(1.05)'
                    : 'scale(1)',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                EN
              </Box>
            </Box>
          </ToggleButton>
        </Tooltip>
        <Tooltip
          title="العربية"
          arrow
          TransitionComponent={Fade}
          placement="top"
        >
          <ToggleButton
            value="ar"
            onMouseEnter={() => setHoveredLang('ar')}
            onMouseLeave={() => setHoveredLang(null)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                transition: 'transform 0.2s',
                transform:
                  hoveredLang === 'ar' && language !== 'ar'
                    ? 'scale(1.05)'
                    : 'scale(1)',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  fontFamily: '"Noto Sans Arabic", "Cairo", sans-serif',
                }}
              >
                AR
              </Box>
            </Box>
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Box>
  );
};

export default LanguageSelector;
